import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Divider, Drawer, Modal, Popconfirm, Row } from "antd";
import { useCallback, useMemo, useState } from "react";
import "./styles.css";
import "../styles.css";
import { cloneDeep } from 'lodash';
import { NetworkSunkhronosCompanyCreate, SunkhronosCompany } from '../../../../utils/types/applications/sunkhronosTypes';
import { General, ISunkhronosMessage, ISunkhronosMobile, ISunkhronosMobileMenu, ISunkhronosModule, ISunkhronosModuleSetting, ISunkhronosTexts, ISunkhronosUserCode, SunkhronosCompanyTypes, SunkhronosMessages, SunkhronosMobile, SunkhronosModules } from './utils';
import { DictionaryString } from '../../../../utils/types/generalTypes';
import Network from '../../../../utils/Network';
import { ConvertNetworkSunkhronosCompany } from '../../../../utils/converters';
import { showNotification } from '../../../../utils/utils';
import FAIcon from '../../../common/FAIcon/FAIcon';
import GeneralCompanyDrawer from './tabs/GeneralCompany';
import ModulesCompanyDrawer from './tabs/ModulesCompany';
import MobileCompanyDrawer from './tabs/MobileCompany';
import MessagesCompanyDrawer from './tabs/MessagesCompany';
import CircleButton from '../../../common/CircleButton/CircleButton';

interface Props {
    open: boolean;
    onCancel: (company?: SunkhronosCompany) => void;
}

interface CompanyData {
    general: General;
    modules: ISunkhronosModule[],
    texts: ISunkhronosTexts,
    user_code: ISunkhronosUserCode;
    mobile: ISunkhronosMobile,
    messages: ISunkhronosMessage[],
}

interface CompanyCredentials {
    username: string;
    password: string;
}

type language = "fr" | "de" | "en";

const CreateCompanyDrawer = (props: Props) => {
    const intl = useIntl();
    const [loading, setLoading] = useState(false);
    const [displayCredentialsModal, setDisplayCredentialsModal] = useState(false);
    const [newCompany, setNewCompany] = useState<SunkhronosCompany | undefined>(undefined);
    const [credentials, setCredentials] = useState<CompanyCredentials | undefined>(undefined);

    const defaultLang = "fr";

    const makeMessage = useCallback((sunkhronosMessages: ISunkhronosMessage[]) => {
        return sunkhronosMessages.map(message => ({
            ...message,
            enabled: message.enabled ?? false,
            enabledImage: message.enabledImage ?? false,
            images: message.images ?? []
        }));
    }, []);

    const makeMobileMenu = useCallback((sunkhronosMobileMenu: ISunkhronosMobileMenu[]) => {
        return sunkhronosMobileMenu.map(menu => ({
            ...menu,
            enabled: menu.enabled ?? false,
        }));
    }, []);

    const getDefaultValue = useCallback((type: string) => {
        switch (type) {
            case 'bool':
                return false;
            case 'number':
                return 1;
            default:
                return '';
        }
    }, []);

    const makeModules = useCallback((sunkhronosModules: ISunkhronosModule[]): ISunkhronosModule[] => {
        return sunkhronosModules.map(module => ({
            ...module,
            enabled: module.enabled ?? false,
            settings: module.settings.map(setting => ({
                ...setting,
                value: setting.value ?? getDefaultValue(setting.type),
            })),
        }));
    }, [getDefaultValue]);

    const messages = useMemo(() => makeMessage(SunkhronosMessages), [makeMessage]);
    const modules = useMemo(() => makeModules(SunkhronosModules), [makeModules]);
    const mobile = useMemo(() => SunkhronosMobile, []);

    const formDataInit = useMemo(() => {
        return {
            general: {
                name: "",
                language: defaultLang,
                type: 2,
                billing: true
            },
            texts: {
                project: "Projet",
                staff_type: "Aptitude"
            },
            user_code: {
                display: "hidden" as "hidden" | "read-only" | "read-write",
                position: 0 as -1 | 0 | 1
            },
            modules: [
                ...modules
            ],
            mobile: {
                ...mobile,
                mobileMenu: makeMobileMenu(mobile.mobileMenu)
            },
            messages: [
                ...messages.map(msg => {
                    return {
                        ...msg,
                        realTitle: msg.title[defaultLang as language],
                        realContent: msg.content[defaultLang as language],
                    } as ISunkhronosMessage;
                }),
            ]
        };
    }, [makeMobileMenu, messages, mobile, modules]);

    const [formData, setFormData] = useState<CompanyData>(formDataInit as CompanyData);

    const updateFormData = useCallback((section: string, data: any) => {

        setFormData(prev => ({ ...prev, [section]: data }));
    }, []);

    const updateFlag = useCallback((updatedGeneralData: General) => {
        const previousFlag = formData.general.language;

        if (previousFlag !== updatedGeneralData.language) {
            const langFrom = previousFlag as language;
            const langTo = updatedGeneralData.language as language;
            setFormData(prev => {
                const updatedMessages = prev.messages.map(msg => {
                    const hasChanged = msg.realContent !== msg.content[langFrom] || msg.realTitle !== msg.title[langFrom];

                    if (!hasChanged) {
                        return {
                            ...msg,
                            realTitle: msg.title[langTo],
                            realContent: msg.content[langTo]
                        };
                    }
                    return msg;
                });

                return {
                    ...prev,
                    messages: updatedMessages,
                    general: updatedGeneralData
                };
            });
        }
    }, [formData.general.language]);

    const updateCompanyType = useCallback((updatedGeneralData: General) => {
        const previousType = formData.general.type;
        const newType = updatedGeneralData.type;

        if (previousType !== newType) {
            setFormData(prev => {
                const updatedModules = prev.modules.map(module => {
                    // Si le nouveau type est CCNT
                    if (newType === SunkhronosCompanyTypes.CCNT_HOTELS) {
                        // Activer le module CCNT, désactiver les autres
                        if (module.key === 'ccnt') {
                            return { ...module, enabled: true };
                        }
                        return { ...module, enabled: false };
                    } else {
                        // Pour les autres types, désactiver le module CCNT
                        if (module.key === 'ccnt') {
                            return { ...module, enabled: false };
                        }
                        return module;
                    }
                });

                return {
                    ...prev,
                    modules: updatedModules,
                    general: updatedGeneralData
                };
            });
        } else {
            // Pas de changement de type, juste mettre à jour general
            setFormData(prev => ({ ...prev, general: updatedGeneralData }));
        }
    }, [formData.general.type]);

    const handleSave = useCallback(() => {
        function buildHierarchicalSettings(settings: ISunkhronosModuleSetting[]): Record<string, any> {
            return settings.reduce((acc, setting) => {
                // Si c’est un booléen
                if (setting.type === 'bool') {
                    // Exclure le booléen s’il est false → parent désactivé
                    if (setting.value === false) {
                        if (!setting.settings) {
                            // Pas d'enfants → on le garde avec la valeur false
                            acc[setting.key] = false;
                        }
                        return acc;
                    }

                    // Booléen activé (value === true)
                    if (setting.settings?.length) {
                        // On garde TOUS les enfants, même à false
                        const nested = setting.settings.reduce((nestedAcc, child) => {
                            console.log(child);
                            if (child.type === 'date') {
                                nestedAcc[child.key] = child.value?.format('YYYY-MM-DD');
                            }
                            else if (child.type === 'bool') {
                                // Si le parent est true, on garde tous les enfants booléens (true ou false)
                                nestedAcc[child.key] = child.value ?? false;
                            } else if (child.value !== undefined) {
                                nestedAcc[child.key] = child.value;
                            }
                            return nestedAcc;
                        }, {} as Record<string, any>);

                        acc[setting.key] = nested;
                    } else {
                        // Pas d'enfants, juste sa propre valeur
                        acc[setting.key] = true;
                    }
                }


                else if (setting.type === 'date') {
                    if (setting.value !== undefined) {
                        acc[setting.key] = setting.value?.format('YYYY-MM-DD');
                    }
                    else {
                        acc[setting.key] = undefined;
                    }
                }
                // Types non-booléens et non-date   
                else if (setting.value !== undefined) {
                    acc[setting.key] = setting.value;
                }

                return acc;
            }, {} as Record<string, any>);
        }

        const getEmptyFields = () => {
            const emptyFields = [];

            if (!formData.general.name) {
                emptyFields.push(intl.formatMessage({ defaultMessage: "name" }));
            }

            if (!formData.general.image) {
                emptyFields.push(intl.formatMessage({ defaultMessage: "logo of the company" }));
            } else if (!formData.mobile.image) {
                formData.mobile.image = formData.general.image;
            }

            return emptyFields;
        };

        const emptyFields = getEmptyFields();

        if (emptyFields.length === 0) {
            const { ...restMobile } = formData.mobile;

            const transformedMessages = formData.messages
                .filter(message => message.enabled !== false)
                .reduce((acc, message) => {

                    const messageObj: any = {
                        title: message.realTitle,
                        content: message.realContent,
                    };
                    if (message.enabledImage && (message.images?.length ?? 0 > 0)) {
                        messageObj.images = message.images;
                    }

                    acc[message.key] = messageObj;

                    return acc;
                }, {} as DictionaryString<Omit<ISunkhronosMessage, 'label'>>);

            const transformedModules = formData.modules.reduce((acc, module) => {
                if (module.enabled === true) {
                    const settings = buildHierarchicalSettings(module.settings);
                    acc[module.key] = settings;
                }
                return acc;
            }, {} as Record<string, any>);

            const payload: NetworkSunkhronosCompanyCreate = {
                generale: formData.general,
                modules: transformedModules,
                user_code: formData.user_code,
                texts: formData.texts,
                ...(Object.keys(transformedMessages).length > 0 && { messages: transformedMessages }),
                mobile: {
                    ...restMobile,
                    menu: formData.mobile.mobileMenu.reduce((acc, item) => {
                        acc[item.key] = item.enabled ?? false;
                        return acc;
                    }, {} as { [key: string]: boolean; }),
                    display: formData.mobile.mobileDisplay.reduce((acc, display) => {
                        acc[display.key] = display.enabled ?? false;
                        return acc;
                    }, {} as { [key: string]: boolean; }),
                    color_primary: formData.mobile.colorMenu[0].color,
                    color_secondary: formData.mobile.colorMenu[1].color,
                    color_button: formData.mobile.colorMenu[2].color,
                }
            };

            setLoading(true);

            Network.createNewCompany(payload).then(
                (res) => {
                    setDisplayCredentialsModal(true);
                    setCredentials(res.data.credentials);
                    setNewCompany(ConvertNetworkSunkhronosCompany(res.data?.company));
                    setLoading(false);
                },
                () => {
                    showNotification(intl.formatMessage({ defaultMessage: 'An error occurred while creating the company.' }), 'error');
                    setLoading(false);
                }
            );
        } else {
            if (emptyFields.length === 1) {
                showNotification(intl.formatMessage({ defaultMessage: 'The {input} is empty' }, { input: emptyFields[0] }), 'error');
            } else {
                showNotification(intl.formatMessage({ defaultMessage: 'The {input1} and {input2} are empty' }, { input1: emptyFields[0], input2: emptyFields[1] }), 'error');
            }
        }
    }, [formData.general, formData.messages, formData.mobile, formData.modules, formData.texts, formData.user_code, intl]);

    const reset = useCallback(() => {
        setLoading(false);
        setCredentials(undefined);
        setDisplayCredentialsModal(false);
        setFormData(formDataInit);
        setNewCompany(undefined);
    }, [formDataInit]);

    const onCancel = useCallback(() => {
        const companyToAdd = cloneDeep(newCompany);
        reset();
        props.onCancel(companyToAdd);
    }, [newCompany, props, reset]);

    return (
        <>
            <Drawer
                title={<><FAIcon prefix='fad' name='industry-windows' /> <FormattedMessage defaultMessage="New company" /></>}
                placement="right"
                width={720}
                onClose={onCancel}
                open={props.open}
                closable={false}
                maskClosable={false}
                destroyOnClose={true}
                footer={[
                    <Col className='flex-between'>
                        <Button key="cancel" onClick={onCancel} disabled={loading}>
                            <FormattedMessage defaultMessage={'Cancel'} />
                        </Button>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <CircleButton
                                type={formData.general.billing ? 'primary' : 'default'}
                                onClick={() => updateCompanyType({ ...formData.general, billing: !formData.general.billing })}
                                icon={<FAIcon prefix='fad' name='money-check-dollar' />}
                                title={intl.formatMessage({ defaultMessage: 'Include in billing' })}
                            />
                            <Button key="save" type='primary' onClick={handleSave} loading={loading}>
                                <FormattedMessage defaultMessage={'Save'} />
                            </Button>
                        </div>
                    </Col>
                ]}
            >
                <GeneralCompanyDrawer
                    generalData={formData.general}
                    textsData={formData.texts}
                    userCode={formData.user_code}
                    updateTexts={(updatedText) => updateFormData('texts', updatedText)}
                    updateUserCode={(updatedUserCode) => updateFormData('user_code', updatedUserCode)}
                    updateData={updateCompanyType}
                    updateFlag={updateFlag}
                />

                <Divider className='divider' />

                <ModulesCompanyDrawer
                    companyType={formData.general.type}
                    modules={formData.modules}
                    updateData={(updatedModules) => updateFormData('modules', updatedModules)}
                />


                <Divider className='divider' />

                <MobileCompanyDrawer
                    mobileData={formData.mobile}
                    updateData={(updatedData) => updateFormData('mobile', updatedData)}
                />


                <Divider className='divider' />

                <MessagesCompanyDrawer
                    messageData={formData.messages}
                    updateData={(data) => updateFormData('messages', data)} />

                <Modal
                    open={displayCredentialsModal}
                    closable={false}
                    maskClosable={false}
                    title={<FormattedMessage defaultMessage={'Company credentials'} />}
                    footer={
                        <Popconfirm
                            title={<FormattedMessage defaultMessage={'Did you save the password'} />}
                            description={<FormattedMessage defaultMessage={"Make sure you saved it, otherwise you'll need to contact the support to change it."} />}
                            okText={<FormattedMessage defaultMessage={'Close'} />}
                            onConfirm={onCancel}
                        >
                            <Button type='primary'>
                                <FormattedMessage defaultMessage={'Close'} />
                            </Button>
                        </Popconfirm>
                    }
                >
                    <Row>
                        <Col xs={{ span: 24 }}>
                            <FormattedMessage defaultMessage={'Please save this password into your password manager. It is the admin access to the freshly created company.'} />
                        </Col>

                        <Col xs={{ span: 24 }}>
                            <Row>
                                <Col xs={{ span: 24 }} className='flex-between'>
                                    <p><FormattedMessage defaultMessage={'Username'} /></p>
                                    <p>{credentials?.username}</p>
                                </Col>

                                <Col xs={{ span: 24 }} className='flex-between'>
                                    <p><FormattedMessage defaultMessage={'Password'} /></p>
                                    <p>{credentials?.password}</p>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Modal>
            </Drawer >
        </>
    );
};

export default CreateCompanyDrawer;
